import React from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";

const NAVY = "#081b33";
const TEAL = "#00a6b2";

const MAIN_ICON_PATH =
  "M769.11,362A79.12,79.12,0,0,1,700,440.5H642.67L601,517.58H533l41.71-77.08,29.78-55.05h87a23.49,23.49,0,1,0,0-47H603.38l-25.4,47L548.21,440.5,530.09,474,506.5,517.58H485.74L462.15,474,444.06,440.5l-12-22.22L420,440.5,401.9,474l-23.59,43.61H357.55L334,474,315.85,440.5l-29.78-55.05-25.4-47-29.78-55.05h67.94l29.77,55.05,25.42,47,13.91,25.72,13.91-25.72,11.21-20.7v0l14.21-26.25L432,292.7l24.77,45.78,9.19,17,16.21,30,13.91,25.72L510,385.45l25.42-47,29.78-55.05H700A79.12,79.12,0,0,1,769.11,362Z";
const TRIANGLE_RIGHT = "577.92 434.6 533.03 517.58 622.82 517.58 577.92 434.6";
const TRIANGLE_LEFT = "393.06 364.74 432.04 292.69 354.08 292.69 393.06 364.74";

/**
 * LoadingScreen1 — web port of mobile's LoadingScreen.jsx: the W/P mark and
 * both triangles draw INWARD as an outline (via the clipPath trick below,
 * same as mobile — a double-width stroke clipped to the shape's own fill
 * area, so only the inward-facing half survives), then cross-fade into a
 * solid fill. The wordmark then reveals in reading order — "Waypoint"
 * wipes in left-to-right (W through the final T), then the two decorative
 * rules either side of the tagline draw in, then "Imports" wipes in the
 * same left-to-right way between them. Text glyphs are solid filled
 * shapes (not a single continuous outline the way the icon is), so
 * "drawing" them is faked with an animated clip-path rectangle that grows
 * left-to-right and reveals whatever's underneath — reads the same as a
 * stroke draw-in without trying to animate every individual letter path.
 * Once everything has settled, the logo breathes gently forever
 * afterward — same idea as this component's original infinite pulse,
 * just picked up only once the whole draw-in sequence has finished.
 *
 * Usage (MUI's sx prop takes an OBJECT, not a CSS string, and sizes the LOGO
 * itself — the wrapper always fills and centers on the viewport):
 *   <LoadingScreen1 />
 *   <LoadingScreen1 sx={{ width: 240 }} />
 */
export default function LoadingScreen1({ sx = {} }) {
  const theme = useTheme();
  // The wordmark/icon were hardcoded to NAVY (#081b33) — on a dark-mode
  // page (this screen fills the viewport with the app's own background,
  // which now follows theme), that's near-black on near-black, essentially
  // invisible. Swapped for a light tone in dark mode; TEAL is left as-is
  // since it already reads fine against both a light and a dark backdrop.
  const MARK_COLOR = theme.palette.mode === "dark" ? "#F4F6FA" : NAVY;
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: 300, ...sx }}>
        <style>{`
          .lw-stroke {
            fill: none;
            stroke-linecap: round;
            stroke-linejoin: round;
          }
          .lw-main-stroke {
            stroke: ${MARK_COLOR};
            stroke-width: 20px;
            stroke-dasharray: 2300;
            stroke-dashoffset: 2300;
            clip-path: url(#lw-main-clip);
            animation: lwMainDraw 1.6s cubic-bezier(0.65,0,0.35,1) forwards,
                       lwFadeOut 0.4s ease forwards 1.6s;
          }
          .lw-main-fill {
            fill: ${MARK_COLOR};
            opacity: 0;
            animation: lwFadeIn 0.4s ease forwards 1.6s;
          }
          .lw-icon-group {
            transform-box: fill-box;
            transform-origin: center;
            animation: lwPulseMark 1.8s ease-in-out infinite 5s;
          }
          .lw-tri-stroke {
            stroke: ${TEAL};
            stroke-width: 20px;
            stroke-dasharray: 280;
            stroke-dashoffset: 280;
            animation: lwTriDraw 0.6s cubic-bezier(0.65,0,0.35,1) forwards 2s,
                       lwFadeOut 0.4s ease forwards 2.6s;
          }
          .lw-tri-right-stroke { clip-path: url(#lw-tri-right-clip); }
          .lw-tri-left-stroke { clip-path: url(#lw-tri-left-clip); }
          .lw-tri-fill {
            fill: ${TEAL};
            opacity: 0;
            animation: lwFadeIn 0.4s ease forwards 2.6s,
                       lwPulseTeal 1.8s ease-in-out infinite 5s;
          }

          /* Icon draw-in (stroke, fill, both triangles) runs 0s-3s;
             wordmark reveal runs 3s-5s: "Waypoint" wipes in, then
             "Imports" wipes in, then both decorative rules draw in last.
             Each reveal rect starts at width 0 (nothing showing through
             the clip) and grows to fully cover its row's bounding box,
             wiping the text in left-to-right. */
          .lw-reveal-rect { animation-timing-function: cubic-bezier(0.65,0,0.35,1); animation-fill-mode: forwards; }
          #lw-waypoint-reveal-rect { animation-name: lwRevealWaypoint; animation-duration: 0.9s; animation-delay: 3s; }
          #lw-imports-reveal-rect { animation-name: lwRevealImports; animation-duration: 0.7s; animation-delay: 3.9s; }
          #lw-rule-left-reveal-rect { animation-name: lwRevealRuleLeft; animation-duration: 0.4s; animation-delay: 4.6s; }
          /* Right rule grows from ITS right edge leftward — x has to
             animate alongside width (x = right edge - width) so the right
             edge stays pinned while the left edge sweeps inward. */
          #lw-rule-right-reveal-rect { animation-name: lwRevealRuleRight; animation-duration: 0.4s; animation-delay: 4.6s; }
          @keyframes lwRevealWaypoint { from { width: 0; } to { width: 670px; } }
          @keyframes lwRevealRuleLeft { from { width: 0; } to { width: 140px; } }
          @keyframes lwRevealRuleRight { from { width: 0; x: 822.61px; } to { width: 140px; x: 682.61px; } }
          @keyframes lwRevealImports { from { width: 0; } to { width: 340px; } }

          @keyframes lwMainDraw { to { stroke-dashoffset: 0; } }
          @keyframes lwTriDraw { to { stroke-dashoffset: 0; } }
          @keyframes lwFadeOut { from { opacity: 1; } to { opacity: 0; } }
          @keyframes lwFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes lwPulseMark {
            0%, 100% { transform: scale(1); }
            50%      { transform: scale(1.035); }
          }
          @keyframes lwPulseTeal {
            0%, 100% { fill: ${TEAL}; }
            50%      { fill: #5be0ea; }
          }
        `}</style>

        <svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
          <defs>
            {/* Clipping the stroke to the shape's OWN fill area is what
                makes it read as growing inward instead of straddling the
                silhouette's true edge — only the inner half of a
                double-width stroke survives the clip. Same technique as
                mobile's LoadingScreen.jsx. */}
            <clipPath id="lw-main-clip">
              <path d={MAIN_ICON_PATH} />
            </clipPath>
            <clipPath id="lw-tri-right-clip">
              <polygon points={TRIANGLE_RIGHT} />
            </clipPath>
            <clipPath id="lw-tri-left-clip">
              <polygon points={TRIANGLE_LEFT} />
            </clipPath>

            {/* Left-to-right wipe reveals for the wordmark — each rect
                starts at width 0 pinned to its row's LEFT edge and grows
                rightward, so whatever's clipped underneath appears to
                "draw in" reading-order instead of just fading in as a
                whole block. */}
            <clipPath id="lw-waypoint-reveal">
              <rect id="lw-waypoint-reveal-rect" className="lw-reveal-rect" x="170" y="570" width="0" height="90" />
            </clipPath>
            <clipPath id="lw-rule-left-reveal">
              <rect id="lw-rule-left-reveal-rect" className="lw-reveal-rect" x="177" y="690" width="0" height="20" />
            </clipPath>
            <clipPath id="lw-rule-right-reveal">
              <rect id="lw-rule-right-reveal-rect" className="lw-reveal-rect" x="822.61" y="690" width="0" height="20" />
            </clipPath>
            <clipPath id="lw-imports-reveal">
              <rect id="lw-imports-reveal-rect" className="lw-reveal-rect" x="330" y="675" width="0" height="50" />
            </clipPath>
          </defs>

          <g className="lw-icon-group">
            <path className="lw-stroke lw-main-stroke" d={MAIN_ICON_PATH} />
            <path className="lw-main-fill" d={MAIN_ICON_PATH} />
            <polygon className="lw-stroke lw-tri-stroke lw-tri-right-stroke" points={TRIANGLE_RIGHT} />
            <polygon className="lw-stroke lw-tri-stroke lw-tri-left-stroke" points={TRIANGLE_LEFT} />
            <polygon className="lw-tri-fill" points={TRIANGLE_RIGHT} />
            <polygon className="lw-tri-fill" points={TRIANGLE_LEFT} />
          </g>

          {/* "Waypoint" name row — wipes in left-to-right (W through the
              final T), starting once the triangles start drawing. */}
          <g clipPath="url(#lw-waypoint-reveal)">
            <polygon fill={MARK_COLOR} points="284.44 577.32 261.66 646.07 248.09 646.07 231.09 595.27 213.92 646.07 200.18 646.07 177.38 577.32 190.65 577.32 207.75 629.62 225.5 577.32 237.3 577.32 254.64 629.9 272.27 577.32 284.44 577.32" />
            <path fill={MARK_COLOR} d="M338.45,577.32H325.88L295,646.07H308l6.83-15.91h34.38L356,646.07h13.46Zm-19.31,42.82L332.07,590l12.87,30.1Z" />
            <polygon fill={MARK_COLOR} points="442.56 577.32 415.54 621.93 415.54 646.07 402.87 646.07 402.87 621.76 375.87 577.32 389.42 577.32 409.74 610.78 429.99 577.32 442.56 577.32" />
            <path fill={MARK_COLOR} d="M515.23,588.61a22.53,22.53,0,0,0-10.08-8.4,38.58,38.58,0,0,0-15.66-2.89H461.2v68.75H474V626.24h15.51a37.24,37.24,0,0,0,15.66-3,22.26,22.26,0,0,0,13.61-21.46A23.84,23.84,0,0,0,515.23,588.61Zm-13.61,23.23q-4.28,3.58-12.72,3.58H474v-27.3H488.9c5.63,0,9.87,1.21,12.72,3.58s4.27,5.76,4.27,10.08S504.47,609.46,501.62,611.84Z" />
            <path fill={MARK_COLOR} d="M576.51,647.05a40.39,40.39,0,0,1-14.78-2.65,35.4,35.4,0,0,1-11.84-7.47,33.89,33.89,0,0,1-7.76-11.24,37.24,37.24,0,0,1,0-28.09,33.49,33.49,0,0,1,7.76-11.19A35.84,35.84,0,0,1,561.68,579a40.28,40.28,0,0,1,14.83-2.65,40.87,40.87,0,0,1,14.73,2.6A34.58,34.58,0,0,1,603,586.31a33.45,33.45,0,0,1,7.71,11.24,37.62,37.62,0,0,1,0,28.24,34.15,34.15,0,0,1-19.44,18.66A40.87,40.87,0,0,1,576.51,647.05Zm0-11.2a25.43,25.43,0,0,0,9.57-1.76,22.71,22.71,0,0,0,7.61-5,23.21,23.21,0,0,0,5.06-7.66,25.39,25.39,0,0,0,1.82-9.73A25.94,25.94,0,0,0,598.8,602a22.31,22.31,0,0,0-5.06-7.66,23.22,23.22,0,0,0-7.66-5,25.23,25.23,0,0,0-9.57-1.76,26.07,26.07,0,0,0-9.68,1.76,22.24,22.24,0,0,0-7.71,5.06,24.87,24.87,0,0,0,0,34.67,22.24,22.24,0,0,0,7.71,5.06A26.27,26.27,0,0,0,576.51,635.85Z" />
            <path fill={MARK_COLOR} d="M638.77,646.07V577.32h12.77v68.75Z" />
            <polygon fill={MARK_COLOR} points="742.78 577.32 742.78 646.07 732.26 646.07 694.45 599.44 694.45 646.07 681.78 646.07 681.78 577.32 692.3 577.32 730.21 623.97 730.21 577.32 742.78 577.32" />
            <path fill={MARK_COLOR} d="M787.07,646.07v-58H764.28v-10.8h58.34v10.8H799.83v58Z" />
          </g>

          {/* Decorative rules — draw in together once "Waypoint" finishes,
              each growing left-to-right from its own left edge. */}
          <g clipPath="url(#lw-rule-left-reveal)">
            <rect fill={TEAL} x="177.38" y="697.25" width="125.31" height="5.66" />
          </g>
          <g clipPath="url(#lw-rule-right-reveal)">
            <rect fill={TEAL} x="697.3" y="697.25" width="125.31" height="5.66" />
          </g>

          {/* "Imports" tagline — wipes in left-to-right once the rules are
              drawn. */}
          <g clipPath="url(#lw-imports-reveal)">
            <path fill={TEAL} d="M339.15,716.08v-34h6.31v34Z" />
            <polygon fill={TEAL} points="401.75 716.08 401.75 693.55 390.65 712.25 387.78 712.25 376.61 693.87 376.61 716.08 370.59 716.08 370.59 682.11 375.8 682.11 389.27 704.61 402.54 682.11 407.73 682.11 407.82 716.08 401.75 716.08" />
            <path fill={TEAL} d="M459.64,687.7a10.89,10.89,0,0,0-5-4.15,18.94,18.94,0,0,0-7.75-1.44H433v34h6.3v-9.79h7.67a18.6,18.6,0,0,0,7.75-1.49,11.31,11.31,0,0,0,5-4.17,11.57,11.57,0,0,0,1.76-6.44A11.71,11.71,0,0,0,459.64,687.7ZM439.26,704l-.3-.36.3-.32Zm13.67-4.87c-1.4,1.19-3.51,1.78-6.29,1.78h-7.38v-13.5h7.38c2.78,0,4.89.59,6.29,1.78a6.09,6.09,0,0,1,2.11,5A6.16,6.16,0,0,1,452.93,699.17Z" />
            <path fill={TEAL} d="M500.12,716.57a20.06,20.06,0,0,1-7.3-1.31,17.49,17.49,0,0,1-5.85-3.69,16.62,16.62,0,0,1-3.83-5.56,18.39,18.39,0,0,1,0-13.88A16.5,16.5,0,0,1,487,686.6a17.65,17.65,0,0,1,5.82-3.66,19.86,19.86,0,0,1,7.33-1.31,20.29,20.29,0,0,1,7.28,1.28,17.15,17.15,0,0,1,5.8,3.64,16.8,16.8,0,0,1,3.81,5.56,17.64,17.64,0,0,1,1.36,7,17.46,17.46,0,0,1-1.36,7,16.82,16.82,0,0,1-9.61,9.22A20.08,20.08,0,0,1,500.12,716.57Zm0-5.53a12.52,12.52,0,0,0,4.73-.88,11.18,11.18,0,0,0,3.76-2.47,11.43,11.43,0,0,0,2.5-3.79,12.47,12.47,0,0,0,.9-4.8,12.9,12.9,0,0,0-.87-4.81,11,11,0,0,0-2.5-3.78,11.53,11.53,0,0,0-3.79-2.48,12.52,12.52,0,0,0-4.73-.87,12.93,12.93,0,0,0-4.78.87,11.12,11.12,0,0,0-3.81,2.5,12.08,12.08,0,0,0-2.52,3.79,12.3,12.3,0,0,0,2.52,13.34,10.84,10.84,0,0,0,3.81,2.5A12.94,12.94,0,0,0,500.12,711Z" />
            <path fill={TEAL} d="M562.28,704.93a5.49,5.49,0,0,0,.51-.21,11.36,11.36,0,0,0,5-4.11,11.49,11.49,0,0,0,1.74-6.42,10.92,10.92,0,0,0-3.85-8.85q-3.85-3.23-10.66-3.23H541.07v34h6.32v-9.91h7.67c.45,0,.88,0,1.32,0l6.92,9.94h6.8Zm-14.89-17.48h7.38c2.78,0,4.87.59,6.29,1.78a6.14,6.14,0,0,1,2.09,5,6.25,6.25,0,0,1-2.09,5c-1.42,1.21-3.51,1.79-6.29,1.79h-7.38Z" />
            <path fill={TEAL} d="M599,716.08V687.45H587.71v-5.34h28.83v5.34H605.28v28.63Z" />
            <path fill={TEAL} d="M647.11,716.57a25.09,25.09,0,0,1-7.59-1.14,17.21,17.21,0,0,1-5.8-2.94l2.18-4.9a17.55,17.55,0,0,0,5.05,2.67,18.36,18.36,0,0,0,6.16,1.07,13.19,13.19,0,0,0,4.32-.59,5.14,5.14,0,0,0,2.43-1.57,3.56,3.56,0,0,0,.77-2.26,3.13,3.13,0,0,0-1.11-2.5,8.2,8.2,0,0,0-2.89-1.5q-1.77-.55-3.93-1t-4.32-1.14a19.39,19.39,0,0,1-4-1.72,7.75,7.75,0,0,1-4-7.26,9.18,9.18,0,0,1,1.48-5.07,10.13,10.13,0,0,1,4.51-3.69,18.63,18.63,0,0,1,7.69-1.38,24.58,24.58,0,0,1,6.07.77,17.71,17.71,0,0,1,5.24,2.24l-2,4.9a18.66,18.66,0,0,0-4.71-2,18.15,18.15,0,0,0-4.66-.65,12,12,0,0,0-4.22.63,5.12,5.12,0,0,0-2.4,1.67,3.89,3.89,0,0,0-.75,2.31,3.14,3.14,0,0,0,1.09,2.5,7.79,7.79,0,0,0,2.86,1.48c1.18.36,2.5.69,4,1s2.89.7,4.32,1.14a18.29,18.29,0,0,1,3.93,1.7,8.19,8.19,0,0,1,2.88,2.76,8.07,8.07,0,0,1,1.1,4.39,9.18,9.18,0,0,1-1.48,5,10.3,10.3,0,0,1-4.54,3.69A18.81,18.81,0,0,1,647.11,716.57Z" />
          </g>
        </svg>
      </Box>
    </Box>
  );
}
